import { cast, castToSnapshot, getSnapshot, Instance, SnapshotIn, types } from 'mobx-state-tree'
import { QuizHelper } from '../helpers/QuizHelper'
import { Quiz } from './Quiz'
import { Resource } from './Resource'
import { Workflow } from './workflow/Workflow'
import { flatten } from 'lodash'

/**
 * Topic
 *
 * The intention here is for this object to be used only on the frontend side. Do not add
 * backend properties to this object unless explicitly used on the frontend side.
 */

export const Topic = types
  .model('Topic')
  .props({
    touched: types.maybe(types.boolean),
    topic_id: types.maybeNull(types.number),
    title: types.maybeNull(types.string),
    quizzes: types.maybeNull(types.array(Quiz)),
    imageUrl: types.maybeNull(types.string),
    resources: types.maybeNull(types.array(Resource)),
    is_locked: types.maybe(types.boolean),
    errors: types.maybeNull(types.array(types.string)),
    keep_item_order: types.boolean,
    selected: types.maybe(types.boolean),
    removingResources: types.frozen(),
    addingResources: types.frozen(),
    certification_enabled: types.maybe(types.boolean),
    workflow_id: types.maybeNull(types.number),
    workflow: types.maybe(Workflow),
    duplicate_name: types.maybe(types.boolean),
    original_name: types.maybeNull(types.string),
  })
  .volatile(() => ({
    imageFile: null as File | null,
  }))
  .actions((self) => {
    return {
      setWorkflow(wf: Instance<typeof Workflow>) {
        self.workflow = wf
      },
      setTitle(title: string) {
        self.title = title
      },
      setKeepItemOrder(keep: boolean) {
        self.keep_item_order = keep
      },
      select() {
        self.selected = true
      },
      deselect() {
        self.selected = false
      },
      selectQuiz(quiz: any) {
        const sel = self.quizzes?.find((q) => Boolean(q.selected))
        if (sel) {
          sel.deselect()
        }
        quiz.select()
      },
      reorderQuizzes(drag: number, drop: number) {
        const temp = self.quizzes?.slice()
        if (temp) {
          temp.splice(drop, 0, temp.splice(drag, 1)[0])
          self.quizzes = cast(temp)
        }
      },
      set<K extends keyof SnapshotIn<typeof self>, T extends SnapshotIn<typeof self>>(
        key: K,
        value: T[K],
      ) {
        self[key] = cast(value)
      },
      addNewQuiz() {
        if (!self.quizzes?.length) {
          self.quizzes = castToSnapshot([QuizHelper.createNewQuiz()])
        } else {
          self.quizzes.push(QuizHelper.createNewQuiz())
        }
      },
      setImage(file: File | null) {
        self.imageUrl = null
        self.imageFile = file
      },
      removeQuiz(quiz: Instance<typeof Quiz>) {
        if (!self.quizzes?.length) {
          return
        }

        self.quizzes.remove(quiz)
      },
      addResource: (resource: Instance<typeof Resource>) => {
        if ((self as Instance<typeof Topic>).hasResource(resource)) {
          return
        }
        if (self.removingResources && self.removingResources?.length) {
          self.removingResources = self.removingResources
            .filter((r) => r !== resource?.source_id)
            .map((r) => r)
        }

        if (self.addingResources && self.addingResources?.length) {
          self.addingResources = [...self.addingResources, resource]
        } else {
          self.addingResources = [resource]
        }

        const identityBrokenResource = Resource.create({
          ...getSnapshot(resource),
          identityBreaker: Math.random() * 9999999999,
        })

        if (!self.resources) {
          self.resources = cast([identityBrokenResource])
        } else {
          self.resources.push(identityBrokenResource)
        }
      },
      removeResource: (resource: Instance<typeof Resource>) => {
        if (!self.resources) {
          return
        }

        let adding,
          remove = [] as any

        if (self.removingResources && self.removingResources?.length) {
          remove = [...self.removingResources, resource.source_id]
        } else {
          remove = [resource.source_id]
        }

        if (self.addingResources && self.addingResources?.length) {
          adding = self.addingResources.filter((r) => r.source_id !== resource.source_id)
        }

        const filteredResources = (
          self.resources ?? ([] as Array<Instance<typeof Resource>>)
        ).filter((r) => r.source_id !== resource.source_id)

        self.resources.replace(filteredResources)

        self.removingResources = remove
        self.addingResources = adding
      },
      hasResource: (resource: Instance<typeof Resource>) => {
        if (!self.resources) {
          return false
        }

        return self.resources.some((r) => r.source_id === resource.source_id)
      },
      toPayload: () => {
        const topic = getSnapshot(self)
        return {
          ...topic,
          is_locked: false,
          errors: [],
          certification_enabled: topic.certification_enabled ?? false,
          quizzes: (self.quizzes ?? ([] as Array<Instance<typeof Quiz>>)).map((quiz, index) => {
            return { ...quiz.toPayload(), order: self.keep_item_order ? index + 1 : null }
          }),
        }
      },
      addQuizzes(quizzes: Array<Instance<typeof Quiz>>) {
        // create questionObjects then filter out questions that are already on this quiz
        if (!self.quizzes) {
          self.quizzes = cast(quizzes)
        } else {
          const quizzesToAdd = quizzes.filter(
            (q) => !q.quiz_id || self.quizzes!.some((quiz) => quiz.quiz_id !== q.quiz_id),
          )
          self.quizzes.replace(self.quizzes.concat(quizzesToAdd))
        }
      },
    }
  })
  .views((self) => ({
    get quizResources() {
      const r = [] as any
      self.quizzes?.map((q) => {
        if (q.resources?.length) {
          r.push(...q.resources)
        }
      })
      return r
    },
    get new() {
      return !self.topic_id
    },
    get cardNames() {
      return flatten(
        self.quizzes?.map((quiz) => {
          return quiz.questions
            ?.map((q) => {
              return q.card_name.replace(/\s+/g, '')?.toLowerCase()
            })
            .filter((n) => Boolean(n))
        }),
      )
    },
    get selectedQuiz() {
      return self.quizzes?.find((quiz) => Boolean(quiz.selected))
    },
    get approved() {
      const notApproved = self.quizzes?.filter((q) => !q.approved)
      return (
        notApproved?.length === 0 &&
        self.title &&
        self.title.length > 0 &&
        self.quizzes &&
        self.quizzes?.length > 0
      )
    },
    get formErrors() {
      const errors: any = {}
      if (!self.title?.trim()) {
        errors.emptyTitle = {
          type: 'error',
          message: 'Topic name cannot be blank.',
        }
      }
      if (self.duplicate_name) {
        errors.duplicateTopicTitle = {
          type: 'error',
          message: 'This name is already in use.',
        }
      }
      return errors
    },
    get allErrors() {
      const err = [] as any
      if (self.quizzes) {
        self.quizzes.map((quiz) => {
          err.push(...quiz.allErrors)
        })
      }
      return [...Object.values(this.formErrors), ...err]
    },
    get allWarnings() {
      const err = [] as any
      if (self.quizzes) {
        self.quizzes.map((quiz) => {
          err.push(...quiz.allWarnings)
        })
      }
      return err
    },
    get image() {
      if (self.imageFile) {
        return URL.createObjectURL(self.imageFile)
      } else if (self.imageUrl) {
        return self.imageUrl
      } else {
        return null
      }
    },
  }))
